import React, { useState, useMemo, useCallback, useRef, memo } from 'react';
import { GoogleMap, useLoadScript, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import toast from 'react-hot-toast';

const libraries = ['places']; // Specify 'places' library for autocomplete

const containerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
  border: '1px solid #e5e7eb'
};

const center = {
  lat: 26.9124, // Jaipur, India
  lng: 75.7873,
};

function ProfessionalLocationPicker({ onLocationSelect }) {
  // Memoize the load script configuration to prevent reloads
  const loadScriptConfig = useMemo(() => ({
    googleMapsApiKey: 'AIzaSyBB7eCnq1g88yxps20vrHfiW3QC07G3LPU',
    libraries,
  }), []);

  const { isLoaded, loadError } = useLoadScript(loadScriptConfig);

  if (loadError) {
    return (
      <div style={{
        width: '100%',
        height: '400px',
        backgroundColor: '#f3f4f6',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{ fontSize: '16px', color: '#ef4444', fontWeight: '600' }}>
          ❌ Error loading Google Maps
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
          Please check your internet connection and try again
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{
        width: '100%',
        height: '400px',
        backgroundColor: '#f3f4f6',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f4f6',
          borderTop: '4px solid #ff6b35',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ fontSize: '16px', color: '#374151', fontWeight: '600' }}>
          Loading Google Maps...
        </div>
      </div>
    );
  }

  return <MapComponent onLocationSelect={onLocationSelect} />;
}

const MapComponent = memo(function MapComponent({ onLocationSelect }) {
  const [currentPosition, setCurrentPosition] = useState(center);
  const [address, setAddress] = useState('');
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    console.log('Google Map loaded successfully');
    
    // Try to get user's current location on map load
    if (navigator.geolocation) {
      setIsDetectingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCurrentPosition(userLocation);
          map.panTo(userLocation);
          map.setZoom(15);
          reverseGeocode(userLocation);
          setIsDetectingLocation(false);
          toast.success('Location detected successfully!');
        },
        (error) => {
          console.log("Geolocation denied or unavailable:", error);
          setIsDetectingLocation(false);
          toast.error('Could not detect your location. Please select manually.');
        },
        { 
          enableHighAccuracy: true, 
          timeout: 10000, 
          maximumAge: 300000 
        }
      );
    } else {
      console.log("Geolocation is not supported by this browser.");
      toast.error('Geolocation not supported by this browser.');
    }
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const onMapClick = useCallback((event) => {
    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    setCurrentPosition(newPosition);
    reverseGeocode(newPosition);
  }, []);

  const onPlacesChanged = useCallback(() => {
    const places = searchBoxRef.current.getPlaces();
    if (places.length === 0) return;

    const place = places[0];
    const newPosition = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setCurrentPosition(newPosition);
    setAddress(place.formatted_address);
    mapRef.current.panTo(newPosition);
    mapRef.current.setZoom(15);
    
    // Notify parent component
    if (onLocationSelect) {
      onLocationSelect({
        address: place.formatted_address,
        lat: newPosition.lat,
        lng: newPosition.lng,
        coordinates: newPosition
      });
    }
  }, [onLocationSelect]);

  const reverseGeocode = async (coords) => {
    if (!window.google || !window.google.maps) {
      console.error('Google Maps API not available for reverse geocoding');
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: coords }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const formattedAddress = results[0].formatted_address;
        setAddress(formattedAddress);
        
        // Notify parent component
        if (onLocationSelect) {
          onLocationSelect({
            address: formattedAddress,
            lat: coords.lat,
            lng: coords.lng,
            coordinates: coords
          });
        }
      } else {
        console.error('Reverse geocoding failed:', status);
        setAddress('Address not found');
      }
    });
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser.');
      return;
    }

    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCurrentPosition(userLocation);
        mapRef.current.panTo(userLocation);
        mapRef.current.setZoom(15);
        reverseGeocode(userLocation);
        setIsDetectingLocation(false);
        toast.success('Location detected successfully!');
      },
      (error) => {
        console.error("Error getting user location:", error);
        setIsDetectingLocation(false);
        
        let errorMessage = 'Failed to detect location.';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            break;
        }
        toast.error(errorMessage);
      },
      { 
        enableHighAccuracy: true, 
        timeout: 10000, 
        maximumAge: 300000 
      }
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Search Box */}
      <StandaloneSearchBox
        onLoad={(ref) => (searchBoxRef.current = ref)}
        onPlacesChanged={onPlacesChanged}
      >
        <input
          type="text"
          placeholder="Search for an address..."
          style={{
            boxSizing: `border-box`,
            border: `2px solid #ff6b35`,
            width: `300px`,
            height: `40px`,
            padding: `0 16px`,
            borderRadius: `8px`,
            boxShadow: `0 2px 8px rgba(0, 0, 0, 0.1)`,
            fontSize: `14px`,
            outline: `none`,
            textOverflow: `ellipses`,
            position: "absolute",
            left: "50%",
            marginLeft: "-150px",
            marginTop: "16px",
            zIndex: "10",
            backgroundColor: 'white',
            fontWeight: '500'
          }}
        />
      </StandaloneSearchBox>

      {/* Detect Location Button */}
      <button
        onClick={handleDetectLocation}
        disabled={isDetectingLocation}
        style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          zIndex: '10',
          padding: '8px 16px',
          backgroundColor: isDetectingLocation ? '#9ca3af' : '#ff6b35',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600',
          cursor: isDetectingLocation ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        {isDetectingLocation ? (
          <>
            <div style={{
              width: '12px',
              height: '12px',
              border: '2px solid #f3f4f6',
              borderTop: '2px solid #ff6b35',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Detecting...
          </>
        ) : (
          <>
            📍 Detect Location
          </>
        )}
      </button>

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={currentPosition}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={onMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
      >
        <Marker 
          position={currentPosition}
          icon={{
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="#ff6b35" stroke="white" stroke-width="4"/>
                <circle cx="20" cy="20" r="8" fill="white"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20)
          }}
        />
      </GoogleMap>

      {/* Address Display */}
      {address && (
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
            Selected Address:
          </div>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {address}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{
        marginTop: '12px',
        padding: '8px 12px',
        backgroundColor: '#fef3c7',
        borderRadius: '6px',
        border: '1px solid #f59e0b',
        fontSize: '12px',
        color: '#92400e',
        textAlign: 'center'
      }}>
        💡 Click anywhere on the map or search for an address to select your location
      </div>
    </div>
  );
});

export default memo(ProfessionalLocationPicker);
